import gtagEventEmitter from "helpers/gtagEventEmitter";

/**
 * Fires when the review section scrolls into viewport (>= 50% visible).
 */
export const trackReviewSectionViewed = ({
  propertyId,
  cityId,
  totalReviewCount,
  aiSummaryRendered,
}: {
  propertyId: string | number;
  cityId: string | number;
  totalReviewCount: number;
  aiSummaryRendered: boolean;
}) => {
  gtagEventEmitter({
    action: "review_section_viewed",
    category: "reviews",
    property_id: propertyId,
    city_id: cityId,
    total_review_count: totalReviewCount,
    ai_summary_rendered: aiSummaryRendered,
  });
};

/**
 * Fires when user interacts with the AI summary tooltip/info icon.
 */
export const trackAiSummaryInteracted = ({
  propertyId,
  actionType,
}: {
  propertyId: string | number;
  actionType: "HOVER" | "CLICK" | "TOOLTIP_OPEN";
}) => {
  gtagEventEmitter({
    action: "ai_summary_interacted",
    category: "reviews",
    property_id: propertyId,
    action_type: actionType,
  });
};

/**
 * Fires when user expands the full parameter/category drawer.
 */
export const trackParameterDrawerExpanded = ({
  propertyId,
  initialVisibleParameters,
}: {
  propertyId: string | number;
  initialVisibleParameters: string[];
}) => {
  gtagEventEmitter({
    action: "parameter_drawer_expanded",
    category: "reviews",
    property_id: propertyId,
    initial_visible_parameters: initialVisibleParameters,
  });
};

/**
 * Fires when user clicks the "Helpful" button on a review.
 */
export const trackHelpfulButtonClicked = ({
  reviewId,
  propertyId,
  isVerifiedStay,
  priorVoteCount,
}: {
  reviewId: string;
  propertyId: string | number;
  isVerifiedStay: boolean;
  priorVoteCount: number;
}) => {
  gtagEventEmitter({
    action: "helpful_button_clicked",
    category: "reviews",
    review_id: reviewId,
    property_id: propertyId,
    is_verified_stay: isVerifiedStay,
    prior_vote_count: priorVoteCount,
  });
};

/**
 * Once per page load that arrived on a review deep link. `fallback` means the link named a
 * review or photo that could not be honoured, so the user landed on the reviews view instead.
 */
export const trackReviewLinkLanded = ({
  propertyId,
  device,
  fallback,
  linkType,
}: {
  propertyId: string | number;
  device: "desktop" | "mobile";
  fallback: boolean;
  /** Which view opened: "media" for the gallery, "drawer" for the reviews panel. */
  linkType: "drawer" | "media";
}) => {
  gtagEventEmitter({
    action: "review_link_landed",
    category: "reviews",
    property_id: propertyId,
    device,
    fallback,
    link_type: linkType,
  });
};

/**
 * Every time a review photo opens in the expanded view. `entry` is "shared_url" only for the
 * open that came from the URL on load, "click" for every in-page open.
 */
export const trackReviewImageOpened = ({
  propertyId,
  reviewId,
  img,
  device,
  entry,
}: {
  propertyId: string | number;
  reviewId: string;
  img: number;
  device: "desktop" | "mobile";
  entry: "click" | "shared_url";
}) => {
  gtagEventEmitter({
    action: "review_image_opened",
    category: "reviews",
    property_id: propertyId,
    review_id: reviewId,
    img,
    device,
    entry,
  });
};
